import { useCallback, useMemo, useRef, useState } from 'react'
import { useService } from '../../../hooks/use-service'
import { NotificationService } from '../../../services'
import { CompositeImage, INotificationFieldSuggestions } from '../../../interfaces/notification-field-suggestions'
import { DomainDto } from '../../../dtos/domain'
import { GenerateNotificationVariantsRequest } from '@pushly/models/lib/structs'
import { useCompositeImageLoader } from './use-composite-image-loader'
import { NotificationVariantModel } from '../../../models/notification/notification-variant.model'
import { pause } from '@pushly/aqe/lib/utils'

export type PushlyAiApi = {
    readonly isGenerating: boolean
    generateNotificationVariant(req: NotificationVariantModel): Promise<INotificationFieldSuggestions | null>
    generateNotificationVariants(req: NotificationVariantModel): Promise<INotificationFieldSuggestions[] | null>
    /** Mark specific variant indices as used so auto-fill skips them */
    markVariantsUsed(indices: number[]): void
}

type PageAnalysisDomain = Pick<DomainDto, 'id' | 'mediaQuality'>

const buildResponse = (data: INotificationFieldSuggestions | null): INotificationFieldSuggestions | null => {
    if (!data || Object.values(data).every((v) => (Array.isArray(v) ? !v.length : v === null))) return null
    return data
}

const buildArrayResponse = (data: INotificationFieldSuggestions[] | null): INotificationFieldSuggestions[] | null => {
    if (!data || data.length === 0) return null
    return data.filter((variant) => !Object.values(variant).every((v) => (Array.isArray(v) ? !v.length : v === null)))
}

const buildGenerationRequest = (domainId: number, source: NotificationVariantModel) => {
    const audience = source.getAudience()
    const content = source.getContent()

    return GenerateNotificationVariantsRequest.from({
        // The current iteration of Pushly AI is restricted to the legacy flow.
        flow: 'legacy',
        domain_id: domainId,
        // The current iteration of Pushly AI is restricted to a single segment ID.
        segment_id: audience.getSegmentIds()?.[0]!,
        // Request all four variations at once and then cache them.
        num_variations: 4,
        // Default fewshot for segment history to seven days.
        fewshot_max_days: 7,
        // v1 Pushly AI in pufferfish does not support generating variants
        // with custom content. ONLY the landing URL is sent for generation
        // requests.
        content: {
            landing_url: content.getDefaultContent().getLandingUrl()!,
            // title: sourceContent.getDefaultContent().getTitle() ?? undefined,
            // body: sourceContent.getDefaultContent().getBody() ?? undefined,
            // keywords: sourceContent.getDefaultContent().getKeywords() ?? undefined,
        },
        // The current iteration only allows a single generation request per domain.
        skip_cache: false,
    })
}

export const usePushlyAi = ({ id: domainId, mediaQuality = 40 }: PageAnalysisDomain): PushlyAiApi => {
    if (!domainId) throw new Error('usePushlyAi: Domain ID could not be determined')

    const notifSvc = useService(NotificationService)

    const [isGenerating, setIsGenerating] = useState(false)
    const isGeneratingRef = useRef(false)

    // Helper to keep ref and state in sync. The ref is the source of truth
    // inside the async callback (avoids stale closure), while the state
    // drives re-renders for consumers that read `isGenerating`.
    const setGenerating = useCallback((value: boolean) => {
        isGeneratingRef.current = value
        setIsGenerating(value)
    }, [])

    const lastReqRef = useRef<GenerateNotificationVariantsRequest | null>(null)
    const resultsCacheRef = useRef<INotificationFieldSuggestions[]>([])
    // Tracks which variant indices have been consumed by either auto-fill (single)
    // or the advanced review drawer (all). When all indices are used the set is
    // cleared so the cycle restarts from index 0.
    const usedIndicesRef = useRef<Set<number>>(new Set())

    const loadCompositeImage = useCompositeImageLoader({ id: domainId, mediaQuality })

    const generateVariants = useCallback(
        async <T extends 'single' | 'all'>(
            source: NotificationVariantModel,
            mode: T,
        ): Promise<
            T extends 'single' ? INotificationFieldSuggestions | null : INotificationFieldSuggestions[] | null
        > => {
            type ResponseType = T extends 'single'
                ? INotificationFieldSuggestions | null
                : INotificationFieldSuggestions[] | null

            if (isGeneratingRef.current) {
                return (mode === 'single' ? buildResponse(null) : buildArrayResponse(null)) as ResponseType
            }

            try {
                setGenerating(true)

                const req = buildGenerationRequest(domainId, source)

                // Check if we can use cached results with circular array pattern (single mode only)
                if (mode === 'single' && lastReqRef.current?.equals(req) && resultsCacheRef.current.length) {
                    await pause(500)

                    const resLength = resultsCacheRef.current.length

                    // If all indices have been used, clear the set to restart the cycle
                    if (usedIndicesRef.current.size >= resLength) {
                        usedIndicesRef.current.clear()
                    }

                    // Find the next unused index starting from 0
                    let nextIndex = 0
                    while (usedIndicesRef.current.has(nextIndex) && nextIndex < resLength) {
                        nextIndex++
                    }

                    usedIndicesRef.current.add(nextIndex)

                    return buildResponse(resultsCacheRef.current[nextIndex]) as ResponseType
                }

                // If the req matches the cached one and we have results, return cached results (all mode)
                if (mode === 'all' && lastReqRef.current?.equals(req) && resultsCacheRef.current.length) {
                    return buildArrayResponse(resultsCacheRef.current) as ResponseType
                }

                // Make API request for all 4 variants
                const res = await notifSvc.generateNotificationVariants(domainId, req, {
                    showLoadingScreen: false,
                    cancellationKey: 'pushly-ai.generateNotificationVariants',
                })

                if (!res.ok || !res.data) {
                    return (mode === 'single' ? buildResponse(null) : buildArrayResponse(null)) as ResponseType
                }

                // Preload images once for each unique image URL
                const variantCompositeImageMap: Record<string, CompositeImage> = {}
                const uqVariantImageURLs = Array.from(
                    new Set(res.data.filter((item) => !!item.image).map(({ image }) => image!)),
                )
                await Promise.all(
                    uqVariantImageURLs.map(async (imageUrl) => {
                        try {
                            const compositeImage = await loadCompositeImage(imageUrl)
                            if (compositeImage) {
                                variantCompositeImageMap[imageUrl] ??= compositeImage
                            }
                        } catch {
                            console.warn('[usePushlyAi] Failed to preload composite image', imageUrl)
                        }
                    }),
                )

                // Convert to INotificationFieldSuggestions with composite images
                const allVariants = res.data.map<INotificationFieldSuggestions>((item) => {
                    let variantCompositeImage: CompositeImage | null = null
                    if (item.image && variantCompositeImageMap[item.image]) {
                        variantCompositeImage = variantCompositeImageMap[item.image]
                    }

                    return {
                        title: item.title,
                        body: item.body,
                        imageUrl: variantCompositeImage?.url ?? item.image,
                        compositeImage: variantCompositeImage,
                        keywords: item.keywords,
                        tone: item.tone,
                        reason: item.reason,
                    }
                })

                // Cache the results and request
                lastReqRef.current = req.clone()
                resultsCacheRef.current = allVariants.reverse()

                // Return based on mode
                if (mode === 'single') {
                    usedIndicesRef.current.clear()
                    usedIndicesRef.current.add(0)
                    return buildResponse(resultsCacheRef.current[0]) as ResponseType
                } else {
                    usedIndicesRef.current.clear()
                    return buildArrayResponse(resultsCacheRef.current) as ResponseType
                }
            } finally {
                setGenerating(false)
            }
        },
        [domainId, loadCompositeImage, notifSvc],
    )

    const generateNotificationVariant = useCallback(
        async (source: NotificationVariantModel): Promise<INotificationFieldSuggestions | null> => {
            return generateVariants(source, 'single')
        },
        [generateVariants],
    )

    const generateNotificationVariants = useCallback(
        async (source: NotificationVariantModel): Promise<INotificationFieldSuggestions[] | null> => {
            return generateVariants(source, 'all')
        },
        [generateVariants],
    )

    const markVariantsUsed = useCallback((indices: number[]) => {
        for (const idx of indices) {
            usedIndicesRef.current.add(idx)
        }
    }, [])

    return useMemo(
        () => ({
            generateNotificationVariant,
            generateNotificationVariants,
            markVariantsUsed,
            isGenerating,
        }),
        [generateNotificationVariant, generateNotificationVariants, markVariantsUsed, isGenerating],
    )
}
